// "Install the app" flow for https://cellgame.io/install and the lobby button.
// Browsers never install silently: Chromium (Chrome, Edge, Android) gives us beforeinstallprompt, which we show
// from our own button; iOS Safari only supports Share > Add to Home Screen, so it gets the two steps instead.
interface InstallPrompt extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }

export const isStandalone = () =>
  matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: fullscreen)').matches
  || (navigator as Navigator & { standalone?: boolean }).standalone === true;
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isInstallLink = (path = location.pathname, search = location.search) =>
  path === '/install' || path === '/install/' || new URLSearchParams(search).has('install');

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;

export class Installer {
  private deferred: InstallPrompt | null = null;
  private readonly card = $('installCard');
  private readonly text = $('installText');
  private readonly ios = $('installIos');
  private readonly btn = $<HTMLButtonElement>('installBtn');
  private readonly lobbyBtn = $<HTMLButtonElement>('installLobby');

  constructor(private readonly onClick: () => void = () => {}) {
    addEventListener('beforeinstallprompt', e => {
      e.preventDefault(); // keep the browser's mini-infobar away; our button shows the prompt
      this.deferred = e as InstallPrompt;
      this.refresh();
    });
    addEventListener('appinstalled', () => { this.deferred = null; this.installed(); });
    this.btn?.addEventListener('click', () => { this.onClick(); void this.install(); });
    this.lobbyBtn?.addEventListener('click', () => { this.onClick(); this.open(); void this.install(); });
    $('installPlay')?.addEventListener('click', () => { this.onClick(); this.close(); });
    this.refresh();
    if (isInstallLink()) {
      history.replaceState(null, '', '/' + location.search.replace(/([?&])install(=[^&]*)?&?/, '$1').replace(/[?&]$/, ''));
      if (!isStandalone()) this.open();
    }
  }

  open(): void {
    this.card?.classList.remove('hidden');
    this.refresh();
    // Chromium can take a moment to decide the app is installable; if it never does, explain the fallback
    setTimeout(() => this.refresh(true), 2500);
  }
  close(): void { this.card?.classList.add('hidden'); }

  private async install(): Promise<void> {
    if (!this.deferred) { this.refresh(true); return; }
    const p = this.deferred;
    this.deferred = null; // a prompt can be shown once
    await p.prompt();
    const { outcome } = await p.userChoice;
    if (outcome === 'accepted') this.installed();
    else this.say('No problem. You can install it later from this page, or just play here.');
    this.refresh();
  }

  private installed(): void {
    this.say('Installed! CellGame opens in its own window. You can also start it from your home screen or apps.');
    if (this.btn) this.btn.classList.add('hidden');
    this.lobbyBtn?.classList.add('hidden');
    setTimeout(() => this.close(), 4000);
  }

  // waited: the 2.5 s grace passed without a prompt
  private refresh(waited = false): void {
    const standalone = isStandalone(), ios = isIos();
    this.lobbyBtn?.classList.toggle('hidden', standalone || !(this.deferred || ios));
    if (!this.card || this.card.classList.contains('hidden')) return;
    this.ios?.classList.toggle('hidden', !ios);
    this.btn?.classList.toggle('hidden', ios || standalone || (!this.deferred && waited));
    if (this.btn) this.btn.disabled = !this.deferred;
    if (standalone) this.say('You\'re already playing the installed app.');
    else if (ios) this.say('Add CellGame to your home screen to play full screen:');
    else if (this.deferred) this.say('One tap and CellGame lives on your home screen or dock, full screen, no browser bars.');
    else if (waited) this.say('Already installed, or your browser installs from its menu: look for "Install CellGame" or the install icon in the address bar.');
    else this.say('Getting the installer ready…');
  }
  private say(t: string): void { if (this.text) this.text.textContent = t; }
}
